import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  FileText,
  RefreshCw,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  Calendar,
  CreditCard,
  AlertCircle,
  Copy,
  ExternalLink,
  Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = "/api";
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending_review: 0,
    approved: 0,
    rejected: 0,
    payment_pending: 0,
    payment_confirmed: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [filter, isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      setLoginError('');
    } else {
      setLoginError('Invalid password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/submissions${filter !== 'all' ? `?status=${filter}` : ''}`),
        fetch(`${API}/admin/stats`)
      ]);

      const subsData = await subsRes.json();
      const statsData = await statsRes.json();

      setSubmissions(subsData.submissions || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleReview = async (submissionId, action) => {
    setActionLoading(true);

    try {
      const response = await fetch(`${API}/admin/submissions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: submissionId,
          status: action === 'approve' ? 'approved' : 'rejected',
          notes: reviewNotes
        })
      });

      const data = await response.json();

      if (data.success) {
        if (action === 'approve') {
          alert('✅ Submission approved successfully.');
        } else {
          alert('❌ Submission rejected.');
        }

        setSelectedSubmission(null);
        setReviewNotes('');
        fetchData();
      } else {
        alert('Error: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error processing review');
    }

    setActionLoading(false);
  };

  const handleVerifyPayment = async (submissionId) => {
    if (!confirm('Are you sure you want to verify this payment? Make sure you have confirmed the payment in your bank account.')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/submissions/${submissionId}/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ Payment verified!\n\nRider: ${data.rider_name}\nEmail: ${data.rider_email}\n\nReady for onboarding.`);
        setSelectedSubmission(null);
        fetchData();
      } else {
        alert('Error: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error verifying payment');
    }
    setActionLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      payment_pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      payment_confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
      onboarding: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };

    const labels = {
      pending_review: 'PENDING REVIEW',
      approved: 'APPROVED',
      rejected: 'REJECTED',
      payment_pending: 'AWAITING PAYMENT',
      payment_confirmed: 'PAID',
      onboarding: 'ONBOARDING'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending_review}`}>
        {labels[status] || status?.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPackagePrice = (pkg) => {
    return pkg === 'premium' ? 79 : 49;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#6ABF4B]" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400 mt-2">Enter password to access</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none"
                placeholder="Enter admin password"
                data-testid="admin-password"
              />
            </div>

            {loginError && (
              <p className="text-red-400 text-sm">{loginError}</p>
            )}

            <button
              type="submit"
              className="w-full py-3 bg-[#6ABF4B] text-white font-bold rounded-lg hover:bg-[#5aa93f] transition-colors"
              data-testid="admin-login-btn"
            >
              Access Dashboard
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-400 hover:text-white text-sm">
              ← Back to website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="bg-black border-b border-gray-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-400 text-sm">JD Network Pty Ltd - Submissions Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchData} className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700" data-testid="refresh-btn">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm" data-testid="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-[#111111] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-800 rounded-lg"><FileText className="w-5 h-5 text-gray-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-gray-400 text-sm">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-[#111111] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.pending_review}</p>
                <p className="text-gray-400 text-sm">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-[#111111] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg"><CheckCircle className="w-5 h-5 text-blue-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.approved}</p>
                <p className="text-gray-400 text-sm">Approved</p>
              </div>
            </div>
          </div>
          <div className="bg-[#111111] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg"><CreditCard className="w-5 h-5 text-orange-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.payment_pending}</p>
                <p className="text-gray-400 text-sm">Awaiting Pay</p>
              </div>
            </div>
          </div>
          <div className="bg-[#111111] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg"><DollarSign className="w-5 h-5 text-green-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.payment_confirmed}</p>
                <p className="text-gray-400 text-sm">Paid</p>
              </div>
            </div>
          </div>
          <div className="bg-[#111111] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg"><XCircle className="w-5 h-5 text-red-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.rejected}</p>
                <p className="text-gray-400 text-sm">Rejected</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending_review', label: 'Pending Review' },
            { key: 'approved', label: 'Approved' },
            { key: 'payment_pending', label: 'Awaiting Payment' },
            { key: 'payment_confirmed', label: 'Paid' },
            { key: 'rejected', label: 'Rejected' }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key ? 'bg-[#6ABF4B] text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="bg-[#111111] border border-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="text-left p-4 text-gray-400 font-medium">Name</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Email</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Package</th>
                  <th className="text-left p-4 text-gray-400 font-medium">City</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Date</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-400">Loading...</td></tr>
                ) : submissions.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-400">No submissions found</td></tr>
                ) : (
                  submissions.map((sub) => (
                    <tr key={sub.id} className="border-t border-gray-800 hover:bg-gray-900/50">
                      <td className="p-4 font-medium">{sub.name}</td>
                      <td className="p-4 text-gray-400">{sub.email}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sub.package === 'premium' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-gray-800 text-gray-300'
                        }`}>
                          {sub.package?.toUpperCase()} (${getPackagePrice(sub.package)})
                        </span>
                      </td>
                      <td className="p-4 text-gray-400">{sub.city}</td>
                      <td className="p-4">{getStatusBadge(sub.status)}</td>
                      <td className="p-4 text-gray-400 text-sm">{formatDate(sub.created_at)}</td>
                      <td className="p-4">
                        <button
                          onClick={() => setSelectedSubmission(sub)}
                          className="p-2 bg-gray-800 rounded hover:bg-gray-700"
                          data-testid={`view-${sub.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#111111] border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Submission Details</h2>
                <p className="text-gray-400 text-sm">ID: {selectedSubmission.id}</p>
              </div>
              {getStatusBadge(selectedSubmission.status)}
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-sm">Name</p>
                    <p className="font-medium">{selectedSubmission.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="font-medium">{selectedSubmission.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-sm">Phone</p>
                    <p className="font-medium">{selectedSubmission.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-sm">City</p>
                    <p className="font-medium">{selectedSubmission.city}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-sm">Package</p>
                    <p className="font-medium">{selectedSubmission.package?.toUpperCase()} - ${getPackagePrice(selectedSubmission.package)} AUD</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-sm">Submitted</p>
                    <p className="font-medium">{formatDate(selectedSubmission.created_at)}</p>
                  </div>
                </div>
              </div>

              {selectedSubmission.payment_reference && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-yellow-400">Payment Reference</p>
                    <button
                      onClick={() => copyToClipboard(selectedSubmission.payment_reference)}
                      className="text-yellow-400 hover:text-yellow-300 flex items-center gap-1 text-sm"
                    >
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                  <p className="font-mono text-lg">{selectedSubmission.payment_reference}</p>
                  <p className="text-yellow-400/70 text-xs mt-2">Look for this reference in your bank to verify payment</p>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-3">Documents ({selectedSubmission.files?.length || 0})</h3>
                {selectedSubmission.files && selectedSubmission.files.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSubmission.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-green-400" />
                          <div>
                            <p className="font-medium text-sm">{file.filename}</p>
                            <p className="text-gray-400 text-xs truncate max-w-xs">{file.url}</p>
                          </div>
                        </div>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#6ABF4B] rounded text-sm font-medium hover:bg-[#5aa93f] transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg text-center">
                    <FileText className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No documents uploaded</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-3">Consent</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {selectedSubmission.consent_documents_valid ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                    <span className="text-sm">Documents valid</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSubmission.consent_terms ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                    <span className="text-sm">Terms of Service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSubmission.consent_privacy ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                    <span className="text-sm">Privacy Policy</span>
                  </div>
                </div>
              </div>

              {selectedSubmission.review_notes && (
                <div className="p-4 bg-gray-900 rounded-lg">
                  <p className="text-gray-400 text-sm mb-1">Review Notes</p>
                  <p>{selectedSubmission.review_notes}</p>
                </div>
              )}

              {selectedSubmission.status === 'pending_review' && (
                <div className="border-t border-gray-800 pt-6">
                  <h3 className="font-semibold mb-3">Review Action</h3>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add notes (optional)..."
                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white mb-4"
                    rows={3}
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleReview(selectedSubmission.id, 'approve')}
                      disabled={actionLoading}
                      className="flex-1 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-5 h-5" /> Approve
                    </button>
                    <button
                      onClick={() => handleReview(selectedSubmission.id, 'reject')}
                      disabled={actionLoading}
                      className="flex-1 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-500 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-5 h-5" /> Reject
                    </button>
                  </div>
                </div>
              )}

              {selectedSubmission.status === 'approved' && (
                <div className="border-t border-gray-800 pt-6">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-blue-400" />
                      <p className="font-semibold text-blue-400">Action Required</p>
                    </div>
                    <p className="text-gray-300 text-sm mb-3">
                      Send the payment link to this rider:
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/payment/${selectedSubmission.id}`}
                        className="flex-1 p-2 bg-gray-900 border border-gray-700 rounded text-sm font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(`${window.location.origin}/payment/${selectedSubmission.id}`)}
                        className="p-2 bg-blue-600 rounded hover:bg-blue-500"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={`${window.location.origin}/payment/${selectedSubmission.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-700 rounded hover:bg-gray-600"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {selectedSubmission.status === 'payment_pending' && (
                <div className="border-t border-gray-800 pt-6">
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="w-5 h-5 text-orange-400" />
                      <p className="font-semibold text-orange-400">Payment Claimed</p>
                    </div>
                    <p className="text-gray-300 text-sm">
                      The rider claims to have paid. Check your bank account for a payment with reference:
                      <strong className="text-orange-400 ml-1">{selectedSubmission.payment_reference}</strong>
                    </p>
                  </div>
                  <button
                    onClick={() => handleVerifyPayment(selectedSubmission.id)}
                    disabled={actionLoading}
                    className="w-full py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="verify-payment-btn"
                  >
                    <CheckCircle className="w-5 h-5" /> Verify Payment Received
                  </button>
                </div>
              )}

              {selectedSubmission.status === 'payment_confirmed' && (
                <div className="border-t border-gray-800 pt-6">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <p className="font-semibold text-green-400">Payment Confirmed - Ready for Onboarding</p>
                    </div>
                    <p className="text-gray-300 text-sm mt-2">
                      Contact this rider to begin their Uber Eats / DoorDash registration process.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-800">
              <button
                onClick={() => {
                  setSelectedSubmission(null);
                  setReviewNotes('');
                }}
                className="w-full py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
                data-testid="close-modal-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
